import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LogOut, Menu, X, Repeat, ShieldAlert, ShieldCheck, Mail, Bell } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const navItems = [
  { name: 'Eventos', href: '/equipo-hs/eventos', icon: ShieldAlert },
  { name: 'Planes HS', href: '/equipo-hs/planes-hs', icon: ShieldCheck },
  { name: 'Correos', href: '/equipo-hs/correos', icon: Mail },
];

export const EquipoHsLayout = ({ children }) => {
  const { user, logout, token, hasMultiplePerfiles, switchPerfil } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));
  const [pendingCount, setPendingCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [notifItems, setNotifItems] = useState([]);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API}/incidentes`, { headers: { Authorization: `Bearer ${token}` }, params: { resumen: true } });
        const pend = (res.data.incidentes || []).filter(i => i.estado === 'en_revision');
        setPendingCount(pend.length);
        setNotifItems(pend.slice(0, 8));
      } catch {} // eslint-disable-line no-empty
    };
    fetchNotifs();
    const t = setInterval(fetchNotifs, 45000);
    return () => clearInterval(t);
  }, [token]);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.logo_image) {
          let imgUrl = res.data.logo_image;
          if (!imgUrl.startsWith('http') && !imgUrl.startsWith('data:'))
            imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
          setLogoImage(imgUrl);
          localStorage.setItem('cached_logo_url', imgUrl);
        }
      } catch {} // eslint-disable-line no-empty
    };
    if (!logoImage) fetchLogo();
  }, [token, logoImage]);

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-rose-900 to-slate-900 z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 border-b border-white/10 flex items-center justify-center px-4">
          {logoImage ? (
            <div className="w-[200px] h-[48px] overflow-hidden flex items-center justify-center">
              <img src={logoImage} alt="Logo" className="max-w-full max-h-full object-contain brightness-0 invert"
                onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }}
                data-testid="sidebar-logo" />
            </div>
          ) : (
            <span className="font-bold text-lg text-white tracking-tight">HS Safework</span>
          )}
        </div>

        <div className="px-4 pt-5 pb-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-rose-200/70 font-bold">HS Safework</p>
          <p className="text-sm text-white/90 font-medium mt-0.5">Equipo HS</p>
        </div>

        <nav className="px-3 py-2 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.href;
            return (
              <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? 'bg-rose-500/25 text-white font-medium' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`} data-testid={`nav-${item.name.toLowerCase()}`}>
                <item.icon className="w-4.5 h-4.5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-white/80">{user?.nombre} {user?.apellido}</p>
            <p className="text-[11px] text-white/50 truncate">{user?.email}</p>
          </div>
          {hasMultiplePerfiles && hasMultiplePerfiles() && (
            <button
              onClick={() => { if (switchPerfil()) navigate('/seleccionar-perfil'); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white w-full transition-colors mb-1"
              data-testid="cambiar-perfil-btn">
              <Repeat className="w-4.5 h-4.5" />
              Cambiar de perfil
            </button>
          )}
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-red-500/20 hover:text-red-300 w-full transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-4.5 h-4.5" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      <div className="lg:pl-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 h-14">
          <button className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div />
          <div className="flex items-center gap-2 relative">
            <button onClick={() => setShowNotif(v => !v)} className="relative p-2 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors" data-testid="btn-notif" title="Notificaciones">
              <Bell className="w-5 h-5" />
              {pendingCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </button>
            {showNotif && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotif(false)} />
                <div className="absolute right-0 top-11 z-50 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-rose-900 to-slate-900 text-white px-4 py-2.5 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider">Notificaciones</p>
                    <span className="text-[10px] bg-white/15 px-2 py-0.5 rounded-full">{pendingCount} pendientes</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {notifItems.length === 0 ? (
                      <p className="p-4 text-center text-xs text-slate-400">Sin notificaciones pendientes</p>
                    ) : notifItems.map(n => (
                      <button key={n.id} onClick={() => { navigate('/equipo-hs/eventos'); setShowNotif(false); }}
                        className="w-full text-left px-4 py-3 hover:bg-rose-50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-slate-800 truncate">{n.titulo || '(Sin título)'}</p>
                            <p className="text-[11px] text-slate-500 truncate">{n.empresa_nombre || '—'}</p>
                          </div>
                          <span className="text-[9px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                            En Revisión
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-mono mt-1">{n.codigo || String(n.id).slice(0, 5).toUpperCase()}</p>
                      </button>
                    ))}
                  </div>
                  {pendingCount > notifItems.length && (
                    <div className="border-t border-slate-100 px-4 py-2 text-center">
                      <button onClick={() => { navigate('/equipo-hs/eventos'); setShowNotif(false); }}
                        className="text-xs font-semibold text-rose-700 hover:text-rose-900">
                        Ver todos ({pendingCount})
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <span className="text-sm font-medium text-rose-700 bg-rose-50 px-3 py-1 rounded-full">Equipo HS</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
